

import React, { useState, useEffect } from 'react';
import { fetchTatCounters } from '../doctor/apiConnector';
import { formatTime } from '../doctor/formatTime';
import { Timer } from 'lucide-react';

import Header from '../doctor/Header';
import Sidebar from '../doctor/Sidebar';
import CaseSummaryCard from '../doctor/CaseSummaryCard';
import Filters from '../doctor/Filters';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../Api/apiconnector';
import ChatIcon from "../chat/ChatIcon";
import AutoLogout from '../logout/AutoLogout';

function DoctorDashboard() {
  const [patients, setPatients] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [caseFilter, setCaseFilter] = useState('pending');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModality, setSelectedModality] = useState('All');
  const [selectedDate, setSelectedDate] = useState('');
  const [patientsOriginal, setPatientsOriginal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInstitution, setSelectedInstitution] = useState('All');
  const normalizeInst = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');


  const navigate = useNavigate();

  // Helper to safely parse ISO timestamp to Date (returns null if invalid)
  const parseDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  // Convert backend patient object to frontend-friendly object
  const normalizePatientsFromBackend = (backendPatients) => {
    const now = new Date();

    return backendPatients.map((p) => {
      // keep original backend values
      const receivedOnDb = parseDate(p.recived_on_db) || parseDate(p.received_on_db); // possible keys
      const notesModifiedAt = parseDate(p.notes_modified_at);
      const backendTimeRemaining = (p.time_remaining === null || p.time_remaining === undefined) ? null : Number(p.time_remaining);
      const backendOverdue = (p.overdue_seconds === null || p.overdue_seconds === undefined) ? 0 : Number(p.overdue_seconds);

      // Determine mode:
      // - awaitingHistory: notes_missing (notes_modified_at null) -> we count up elapsed since receivedOnDb
      // - running: backend provided time_remaining (number) -> we use that and let countdown decrement
      // - done: p.is_done true -> show final values (don't increment)
      let awaitingHistory = false;
      let elapsed_seconds = 0;
      let time_remaining = null;
      let overdue_seconds = backendOverdue;

      if (!notesModifiedAt) {
        // Awaiting history behavior: calculate already elapsed seconds since receivedOnDb
        awaitingHistory = true;
        if (receivedOnDb) {
          elapsed_seconds = Math.max(0, Math.floor((now - receivedOnDb) / 1000));
        } else {
          // If receivedOnDb missing, fallback to 0
          elapsed_seconds = 0;
        }
        // while awaiting, time_remaining stays null
        time_remaining = null;
        overdue_seconds = 0; // not applicable while waiting
      } else {
        // notes exist: backend should provide time_remaining; if not, compute fallback
        awaitingHistory = false;

        if (backendTimeRemaining !== null) {
          time_remaining = backendTimeRemaining;
          overdue_seconds = backendOverdue;
        } else {
          // fallback: compute elapsed since notesModifiedAt and set time_remaining negative (overdue) - conservative fallback
          const tatStart = notesModifiedAt;
          const elapsed = Math.max(0, Math.floor((now - tatStart) / 1000));
          // Without tat limit from backend we can't compute exact remaining; set elapsed as overdue_seconds for visibility
          time_remaining = 0;
          overdue_seconds = elapsed;
        }
      }

      return {
        ...p,
        // normalized timestamps (for display or future calculations)
        _receivedOnDb: receivedOnDb ? receivedOnDb.toISOString() : null,
        _notesModifiedAt: notesModifiedAt ? notesModifiedAt.toISOString() : null,

        // flags & runtime fields for frontend timer logic
        awaitingHistory,
        elapsed_seconds,     // valid when awaitingHistory === true
        time_remaining,      // number | null
        overdue_seconds,     // number
      };
    });
  };

  

  const loadPatients = async (cursor = null) => {
  try {
    setLoading(true); // show loader
    const res = await fetchTatCounters(cursor);
    const newPatientsRaw = res.data.results || [];

    const newPatients = normalizePatientsFromBackend(newPatientsRaw);

    setPatientsOriginal(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const unique = newPatients.filter(p => !existingIds.has(p.id));
      return [...prev, ...unique];
    });

    setPatients(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const uniquePatients = newPatients.filter(p => !existingIds.has(p.id));
      return [...prev, ...uniquePatients];
    });

    setNextCursor(res.data.next_cursor);
  } catch (err) {
    console.error("Error fetching patients:", err);
  } finally {
    setLoading(false); // hide loader
  }
};


  // Initial load
  useEffect(() => {
    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer interval: updates awaiting counters (count-up) and running counters (count-down / overdue)
  useEffect(() => {
    const interval = setInterval(() => {
      setPatients(prev =>
        prev.map(p => {
          // If case is reported/done -> don't change timers
          if (p.is_done || p.isDone) {
            return p;
          }

          // If awaiting history -> increment elapsed_seconds
          if (p.awaitingHistory) {
            return { ...p, elapsed_seconds: (p.elapsed_seconds || 0) + 1 };
          }

          // If backend gave a running time_remaining -> decrement until 0 then increment overdue
          if (p.time_remaining !== null && p.time_remaining !== undefined) {
            if (p.time_remaining > 0) {
              return { ...p, time_remaining: p.time_remaining - 1 };
            } else {
              // time_remaining is 0 or negative -> increment overdue_seconds
              return { ...p, time_remaining: 0, overdue_seconds: (p.overdue_seconds || 0) + 1 };
            }
          }

          // Fallback: no explicit fields -> leave unchanged
          return p;
        })
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Toggle expand
  const toggleExpand = idx => {
    setExpandedIndex(expandedIndex === idx ? null : idx);
  };

  // Fetch from backend based on searchTerm (name or patient_id)
  // const fetchSearchResults = async (query) => {
  //   if (!query) return;

  //   const isNumber = /^\d+$/.test(query);

  //   const url = isNumber
  //     ? `${API_BASE_URL}/search_patient/?patient_id=${query}`
  //     : `${API_BASE_URL}/search_patient/?patient_name=${query}`;

  //   try {
  //     const res = await fetch(url);
  //     const data = await res.json();

  //     // backend returns { results: [...] }
  //     if (data?.results) {
  //       const normalized = normalizePatientsFromBackend(data.results);
  //       setPatients(normalized);
  //     }
  //   } catch (err) {
  //     console.error("Search API Error:", err);
  //   }
  // };

  const fetchSearchResults = async (query) => {
  if (!query?.trim()) return;

  const isNumber = /^\d+$/.test(query.trim());

  const url = isNumber
    ? `${API_BASE_URL}/search_patient/?patient_id=${encodeURIComponent(query.trim())}`
    : `${API_BASE_URL}/search_patient/?patient_name=${encodeURIComponent(query.trim())}`;

  try {
    const res = await fetch(url, {
      credentials: 'include', // <-- send session cookies to authenticate user
    });

    if (!res.ok) {
      throw new Error(`HTTP error ${res.status}`);
    }

    const data = await res.json();

    // backend returns { results: [...] }
    if (data?.results) {
      const normalized = normalizePatientsFromBackend(data.results);
      setPatients(normalized);
    } else {
      setPatients([]);
    }
  } catch (err) {
    console.error("Search API Error:", err);
    setPatients([]);
  }
};


  // Sync searchTerm -> debounce fetch
  useEffect(() => {
    if (!searchTerm) {
      // restore full original list when search cleared
      setPatients(patientsOriginal);
      return;
    }

    const delay = setTimeout(() => {
      fetchSearchResults(searchTerm);
    }, 400);

    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, patientsOriginal]);

  // Filtered patients based on selected filter and search/modality/date
  // const filteredPatients = patients
  //   .filter(p => {
  //     const matchSearch = [p.patient_name, p.patient_id, p.study_description]
  //       .some(f => f?.toLowerCase().includes(searchTerm.toLowerCase()));

  //     const matchModality =
  //       selectedModality === 'All' ||
  //       p.modality?.toLowerCase() === selectedModality.toLowerCase();

  //     const matchDate =
  //       !selectedDate || (p._receivedOnDb ? p._receivedOnDb.slice(0, 10) === selectedDate : false);

  //     // caseFilter
  //     if (caseFilter === 'reported') return p.is_done || p.isDone;
  //     if (caseFilter === 'pending') return !(p.is_done || p.isDone);
  //     if (caseFilter === 'review case') return p.review_case === true;

  //      return false; 
  //   });


// // Filtered patients based on selected filter + search + modality + date
// const filteredPatients = patients.filter(p => {
//   const matchSearch = [p.patient_name, p.patient_id, p.study_description]
//     .some(f => f?.toLowerCase().includes(searchTerm.toLowerCase()));

//   const matchModality =
//     selectedModality === 'All' ||
//     p.modality?.toLowerCase() === selectedModality.toLowerCase();

//   const matchDate =
//     !selectedDate ||
//     (p._receivedOnDb && p._receivedOnDb.slice(0, 10) === selectedDate);

//   // caseFilter
//   const matchCase =
//     caseFilter === "reported"
//       ? (p.is_done || p.isDone)
//       : caseFilter === "pending"
//       ? !(p.is_done || p.isDone)
//       : caseFilter === "review case"
//       ? p.review_case === true
//       : true;

//   // FINAL COMBINED FILTER (the missing part!)
//   return matchSearch && matchModality && matchDate && matchCase;
// });


// async function getPresignedUrl(fileKey) {
//   try {
//     const res = await fetch("https://backend.reportingbot.in/api/get-file-url/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ key: fileKey }),
//     });

//     return await res.json(); // returns { url: "https://..." }
//   } catch (err) {
//     console.error("Error fetching presigned URL:", err);
//     return null;
//   }
// }



//   // Summary counts
//   const reportedCases = patients.filter(p => p.is_done || p.isDone).length;
//   const pendingCases = patients.filter(p => !(p.is_done || p.isDone)).length;

//   // Review-specific calculations
//   const reviewCases = patients.filter(p => p.review_case === true);
//   const reviewCount = reviewCases.length;

//   // Reported case TAT Monitor (always shown)
//   const reportedOnTimeCases = patients.filter(p => (p.is_done || p.isDone) && ((p.overdue_seconds || 0) === 0)).length;
//   const reportedTatMonitor = reportedCases > 0
//     ? `${Math.round((reportedOnTimeCases / reportedCases) * 100)}%`
//     : '0%';


// 1. Base Filter: Apply Search, Modality, and Date filters first
  const baseFilteredPatients = patients.filter(p => {
    const matchSearch = [p.patient_name, p.patient_id, p.study_description]
      .some(f => f?.toLowerCase().includes(searchTerm.toLowerCase()));
  
    const matchModality =
      selectedModality === 'All' ||
      p.modality?.toLowerCase() === selectedModality.toLowerCase();
      
    const matchInstitution =
      selectedInstitution === 'All' ||
      normalizeInst(p.institution_name).includes(selectedInstitution);
  
    const matchDate =
      !selectedDate ||
      (p._receivedOnDb && p._receivedOnDb.slice(0, 10) === selectedDate);
  
    return matchSearch && matchModality && matchDate && matchInstitution ;
  });

  // 2. Dynamic Summary Counts: Calculate from the baseFilteredPatients
  const reportedCases = baseFilteredPatients.filter(p => p.is_done || p.isDone).length;
  const pendingCases = baseFilteredPatients.filter(p => !(p.is_done || p.isDone)).length;
  const reviewCount = baseFilteredPatients.filter(p => p.review_case === true).length;

  // Reported case TAT Monitor (now reflects the selected date)
  const reportedOnTimeCases = baseFilteredPatients.filter(p => 
    (p.is_done || p.isDone) && ((p.overdue_seconds || 0) === 0)
  ).length;
  
  const reportedTatMonitor = reportedCases > 0
    ? `${Math.round((reportedOnTimeCases / reportedCases) * 100)}%`
    : '0%';

  // 3. Final Patient List: Apply the selected case/tab filter for the UI render
  const filteredPatients = baseFilteredPatients.filter(p => {
    if (caseFilter === "reported") return p.is_done || p.isDone;
    if (caseFilter === "pending") return !(p.is_done || p.isDone);
    if (caseFilter === "review case") return p.review_case === true;
    return true;
  });

  async function getPresignedUrl(fileKey) {
    try {
      const res = await fetch("https://backend.reportingbot.in/api/get-file-url/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key: fileKey }),
      });
  
      return await res.json(); // returns { url: "https://..." }
    } catch (err) {
      console.error("Error fetching presigned URL:", err);
      return null;
    }
  }

  // Render helpers for TAT display
  const renderTatDisplay = (p) => {
    // awaiting history -> count-up from elapsed_seconds
    if (p.awaitingHistory) {
      return `Awaiting History • ${formatTime(p.elapsed_seconds || 0)}`;
    }

    // if running with a time_remaining value
    if (p.time_remaining !== null && p.time_remaining !== undefined) {
      if (p.time_remaining > 0) {
        return formatTime(p.time_remaining);
      } else {
        return `Overdue: ${formatTime(p.overdue_seconds || 0)}`;
      }
    }

    // fallback
    return '—';
  };

  return (
    <div className="flex h-screen">
       <AutoLogout />   {/* 🔴 AUTO LOGOUT ENABLED */}
      {showSidebar && (
        <Sidebar
          onClose={() => setShowSidebar(false)}
          reportedCases={reportedCases}
          pendingCases={pendingCases}
        />
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        <Header
          onSidebarToggle={() => setShowSidebar(true)}
          reportedTatMonitor={reportedTatMonitor}
        />

        {/* Summary Cards & Filters */}
        <div className="px-1 py-[2px] sticky top-0 bg-white z-10 shadow-sm">
          <div className="flex gap-1 mb-[4px]">
            <CaseSummaryCard value={pendingCases} label="Pending" active={caseFilter === 'pending'} onClick={() => setCaseFilter('pending')} />
            <CaseSummaryCard value={reportedCases} label="Reported" active={caseFilter === 'reported'} onClick={() => setCaseFilter('reported')} />
            <CaseSummaryCard value={reviewCount} label="Review Case" active={caseFilter === 'review case'} onClick={() => setCaseFilter('review case')} />
          </div>

          <Filters
            compact
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedModality={selectedModality}
            setSelectedModality={setSelectedModality}
            selectedInstitution={selectedInstitution}
            setSelectedInstitution={setSelectedInstitution}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </div>

    {/* Patient List */}
<div className="flex-1 overflow-auto px-2 pb-2">
  {loading ? (
    <div className="flex justify-center items-center h-full mt-10">
      <div className="w-10 h-10 border-4 border-t-4 border-red-500 rounded-full animate-spin"></div>
    </div>
  ) : filteredPatients.length === 0 ? (
    <div className="text-center text-gray-500 mt-4 text-sm">
      {caseFilter === 'review case' ? 'No review cases.' : 'No cases found.'}
    </div>
  ) : (
    filteredPatients.map((p, idx) => (
      <div key={p.id}>
        {/* Patient Card */}
        <div
          className={`rounded shadow p-2 mb-2 cursor-pointer hover:shadow-md transition-all duration-200
            ${
              p.urgent
                ? 'bg-red-100 hover:bg-red-200 border border-red-400'
                : p.vip
                ? 'bg-yellow-100 hover:bg-yellow-200 border border-yellow-400'
                : p.Mlc
                ? 'bg-blue-100 hover:bg-blue-200 border border-blue-400'
                : p.is_done || p.isDone
                ? (p.overdue_seconds > 0 ? 'bg-red-50 hover:bg-red-100 border border-red-200' : 'bg-green-100 hover:bg-green-100 border border-green-400')
                : 'bg-white hover:bg-gray-50'
            }
          `}
        >
          <div className="flex justify-between items-start gap-2">
            {/* Left Section */}
            <div className="flex-1">
              <div className="text-[12px] text-gray-400">{p.patient_id}</div>
              <div className="font-medium text-[14px] leading-tight flex flex-wrap items-center gap-1">
                {p.patient_name} • {p.age} y/o • {p.gender} • {p.modality}
                {p.urgent && <span className="text-red-500 ml-1">• Urgent</span>}
                {p.vip && <span className="bg-yellow-200 text-yellow-800 text-[10px] px-1 rounded ml-1 font-semibold">VIP</span>}
                {p.Mlc && <span className="bg-blue-200 text-blue-800 text-[10px] px-1 rounded ml-1 font-semibold">MLC</span>}
              </div>
              <div className="text-[12px] mt-0.5 line-clamp-1">{p.study_description}</div>
              <div className="text-[12px] mt-0.5 line-clamp-1">{p.body_part_examined}</div>
              <div className="text-[14px] mt-0.5 line-clamp-1 text-red-500 font-semibold">{p.review_reason}</div>
            </div>

            {/* Status & Received Time */}
            <div className="flex flex-col items-start px-1 min-w-[100px]">
              <div className={`text-[12px] font-bold ${p.is_done || p.isDone ? 'text-green-600' : 'text-red-600'}`}>
                Status: {p.is_done || p.isDone ? 'Reported' : 'Unreported'}
              </div>
              <div className="text-[11px] text-gray-500">{p._receivedOnDb || p.recived_on_db || p.received_on_db}</div>
            </div>

            {/* Reports & History */}
         {/* Reports & History */}
<div className="flex flex-wrap gap-1 px-1 max-w-[160px] justify-end">
{/* --- PATIENT REPORTS --- */}
{p.patient_reports?.length > 0 ? (
  p.patient_reports.map((report, i) => (
    <button
      key={`report-${i}`}
      className="bg-red-500 text-white px-2 py-[2px] rounded text-[11px] hover:bg-red-600"
      onClick={async (e) => {
        e.stopPropagation();

        // You get file_key directly from backend
        const fileKey = report.file_key;

        if (!fileKey) {
          alert("No file key found");
          return;
        }

        const res = await getPresignedUrl(fileKey);

        if (res?.url) window.open(res.url, "_blank");
        else alert("Error opening report");
      }}
    >
     {`R${i + 1}`}
    </button>
  ))
) : (
  <div className="text-[11px] text-gray-400">No reports</div>
)}


  {/* --- HISTORY FILES --- */}
  {p.history_files?.length > 0 ? (
    p.history_files.map((fileKey, i) => (
      <button
        key={`history-${i}`}
        onClick={async (e) => {
          e.stopPropagation();
          const res = await getPresignedUrl(fileKey); // <-- send key
          if (res?.url) window.open(res.url, "_blank");
          else alert("Error opening history file");
        }}
        className="border border-red-500 text-red-500 px-2 py-[2px] rounded text-[11px] hover:bg-red-50"
      >
        H{i + 1}
      </button>
    ))
  ) : (
    <div className="text-[11px] text-gray-400">No history</div>
  )}

</div>


            {/* TAT & Buttons */}
            <div className="flex flex-col items-end">
              <div className="flex items-center gap-0.5 text-[11px] text-gray-400">
                <Timer size={14} className={`${(p.overdue_seconds || 0) > 0 ? 'text-red-600' : 'text-green-600'}`} /> TAT
              </div>
              <div className={`font-bold text-[12px] ${ (p.overdue_seconds || 0) > 0 ? 'text-red-600' : 'text-green-600' }`}>
                {renderTatDisplay(p)}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/viewer?id=${p.id}`);
                }}
                className="bg-red-500 text-white px-2 py-[2px] mt-1 text-[11px] rounded"
              >
                Report
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(idx);
                }}
                className={`mt-1 px-2 py-[3px] text-[11px] rounded-full border transition-colors duration-200
                  ${expandedIndex === idx
                    ? 'bg-red-100 text-red-600 border-red-300 hover:bg-red-200'
                    : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'}`}
              >
                {expandedIndex === idx ? 'Hide Notes' : 'Show Notes'}
              </button>
            </div>
          </div>
        </div>

        {/* Expanded Details */}
        {expandedIndex === idx && (
          <div className="bg-white rounded shadow p-2 mb-2 text-[12px]">
            <span className="font-semibold">Notes:</span> {p.clinical_notes || p.notes}
          </div>
        )}
      </div>
    ))
  )}

  {/* Show More */}
  {!loading && nextCursor && caseFilter !== 'review case' && (
    <div className="text-center mt-2">
      <button
        onClick={() => loadPatients(nextCursor)}
        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
      >
        Show More
      </button>
    </div>
  )}
</div>
    <ChatIcon role="radiologist" />

      </div>
    </div>
  );
}

export default DoctorDashboard;

